import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker, { DateObject } from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { Search, Image as ImageIcon, X } from 'lucide-react';
import { STORAGE_BASE, API_BASE } from '../config';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input, Field } from './ui/input';
import { Textarea } from './ui/textarea';
import { cn } from '@/lib/utils';

/* =====================================================================
 * Shared Material Design 3 kit for ALL admin pages.
 * Tokens: primary #18181B, surface #fff, outline #E4E4E7, radius 20/12/999.
 * Every admin page (users, orders, transactions, deposits, ...) uses these
 * so the panel looks and behaves identically everywhere + responsive.
 * ===================================================================== */

export const MD = {
  primary: 'hsl(var(--foreground))',
  surface: 'hsl(var(--card))',
  variant: 'hsl(var(--muted))',
  outline: 'hsl(var(--border))',
  onVariant: 'hsl(var(--muted-foreground))',
  success: '#166534',
  successBg: '#DCFCE7',
  warning: '#92400E',
  warningBg: '#FEF3C7',
  error: '#991B1B',
  errorBg: '#FEE2E2',
  info: '#1D4ED8',
  infoBg: '#DBEAFE',
  purple: '#6D28D9',
  purpleBg: '#EDE9FE',
};

export const toastCfg = { position: 'bottom-right', autoClose: 5000, theme: 'colored' };

export function MDCard({ children, style, className, variant = 'outlined' }) {
  return (
    <div
      className={cn(
        'rounded-2xl p-4',
        variant === 'filled' && 'bg-muted/60',
        variant === 'elevated' && 'bg-card shadow-md',
        variant === 'outlined' && 'border border-border bg-card shadow-sm',
        className,
      )}
      style={style}
    >
      {children}
    </div>
  );
}

export function PageHead({ title, sub, count, actions }) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="m-0 text-xl font-black tracking-tight">
          {title}{typeof count === 'number' && <span className="font-medium text-muted-foreground"> ({count.toLocaleString('fa-IR')})</span>}
        </h2>
        {sub && <p className="mt-1 text-[13px] text-muted-foreground">{sub}</p>}
      </div>
      {actions && <div className="flex gap-2">{actions}</div>}
    </div>
  );
}

export function FilterBar({ children }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2">{children}</div>;
}

/* ---- buttons: filled (primary) / tonal / outlined / danger ---- */
export function MBtn({ variant = 'filled', children, style, className, ...rest }) {
  const vf = variant === 'filled' ? 'brand' : variant === 'tonal' ? 'secondary' : variant === 'outlined' ? 'outline' : variant === 'danger' ? 'destructive' : 'brand';
  return <Button variant={vf} className={cn('rounded-full', className)} style={style} {...rest}>{children}</Button>;
}

/* ---- status chip ---- */
export function Chip({ bg, color, children }) {
  return <Badge className="rounded-full border border-black/10 text-[11px] dark:border-white/20" style={{ background: bg, color }}>{children}</Badge>;
}

/* ---- responsive data table (becomes labelled cards on mobile) ---- */
export function MDTable({ head, children, minWidth }) {
  const withLabels = React.Children.map(children, row => {
    if (!React.isValidElement(row) || !head) return row;
    let i = -1;
    const kids = React.Children.map(row.props.children, cell => {
      if (!React.isValidElement(cell)) return cell;
      i += 1;
      if (head[i] == null || cell.props['data-label']) return cell;
      return React.cloneElement(cell, { 'data-label': head[i] });
    });
    return React.cloneElement(row, {}, kids);
  });
  return (
    <div className="md-table overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
      <table className="w-full text-[13px]" style={minWidth ? { minWidth } : undefined}>
        <thead><tr className="bg-muted/50">{head.map((h, i) => (
          <th key={i} className="whitespace-nowrap border-b border-border p-3 text-right text-[11px] font-bold text-foreground">{h}</th>
        ))}</tr></thead>
        <tbody>{withLabels}</tbody>
      </table>
    </div>
  );
}

export function MRow({ children }) {
  return <tr className="border-b border-border transition hover:bg-muted/30">{children}</tr>;
}

export function MCell({ children, bold, muted, ltr, style, ...rest }) {
  return <td className="p-3" style={{ fontWeight: bold ? 700 : 400, color: muted ? 'hsl(var(--muted-foreground))' : undefined, direction: ltr ? 'ltr' : undefined, ...style }} {...rest}>{children}</td>;
}

export function EmptyRow({ colSpan, children }) {
  return <tr><td colSpan={colSpan} className="p-7 text-center text-muted-foreground">{children || 'موردی یافت نشد.'}</td></tr>;
}

export function MPagination({ page, pages, onGo }) {
  if (!pages || pages <= 1) return null;
  const go = p => onGo(Math.min(pages, Math.max(1, p)));
  return (
    <div className="mt-4 flex items-center justify-center gap-2">
      <MBtn variant="outlined" onClick={() => go(page - 1)} disabled={page <= 1}>قبلی</MBtn>
      <span className="text-[12px] font-bold text-muted-foreground">صفحه {page} از {pages}</span>
      <MBtn variant="outlined" onClick={() => go(page + 1)} disabled={page >= pages}>بعدی</MBtn>
    </div>
  );
}

export function MModal({ onClose, title, children, wide }) {
  useEffect(() => {
    const onEsc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onEsc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onEsc); document.body.style.overflow = ''; };
  }, [onClose]);
  return (
    <div className="sheet-overlay fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" onClick={onClose} dir="rtl">
      <div className="sheet-card relative w-full max-h-[90vh] overflow-y-auto rounded-[28px] border border-border bg-card shadow-2xl" style={{ maxWidth: wide ? 860 : 620 }} onClick={e => e.stopPropagation()}>
        <button onClick={onClose} aria-label="بستن"
          className="absolute left-4 top-4 z-10 flex size-9 items-center justify-center rounded-full bg-muted text-foreground transition hover:bg-accent">
          <X size={17} />
        </button>
        {title && <div className="border-b border-border px-6 pb-4 pt-5 text-[17px] font-black">{title}</div>}
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ---- receipt image: button opens the image in a modal (no preload) ---- */
export function ReceiptBtn({ path }) {
  const [open, setOpen] = useState(false);
  if (!path) return <span style={{ color: '#D4D4D8' }}>—</span>;
  const url = STORAGE_BASE + '/' + String(path).replace(/^\//, '');
  return (
    <>
      <MBtn variant="outlined" onClick={() => setOpen(true)} style={{ padding: '6px 14px', fontSize: '0.78rem' }}>
        <ImageIcon size={14} /> مشاهده فیش
      </MBtn>
      {open && (
        <MModal onClose={() => setOpen(false)} title="فیش واریزی">
          <a href={url} target="_blank" rel="noreferrer">
            <img src={url} alt="فیش واریزی" style={{ width: '100%', borderRadius: 12, border: '1px solid #E4E4E7', display: 'block' }} />
          </a>
        </MModal>
      )}
    </>
  );
}

/* ---- select with live search (select2-like): type to filter, clearable ---- */
const selectStyles = {
  control: (b, s) => ({ ...b, borderRadius: 10, border: s.isFocused ? '1.5px solid var(--ring)' : '1.5px solid var(--input)', boxShadow: s.isFocused ? '0 0 0 3px color-mix(in srgb, var(--ring) 15%, transparent)' : 'none', fontFamily: 'inherit', fontSize: '0.82rem', minHeight: 44, background: 'var(--field)', color: 'var(--foreground)', '&:hover': { borderColor: 'var(--ring)' } }),
  menu: b => ({ ...b, fontFamily: 'inherit', fontSize: '0.82rem', zIndex: 70, textAlign: 'right', background: 'var(--popover)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }),
  option: (b, s) => ({ ...b, textAlign: 'right', fontFamily: 'inherit', background: s.isSelected ? 'var(--accent)' : s.isFocused ? 'color-mix(in srgb, var(--accent) 55%, transparent)' : 'transparent', color: s.isSelected ? 'var(--accent-foreground)' : 'var(--foreground)', cursor: 'pointer' }),
  singleValue: b => ({ ...b, fontFamily: 'inherit', color: 'var(--foreground)' }),
  placeholder: b => ({ ...b, fontFamily: 'inherit', color: 'var(--muted-foreground)' }),
  input: b => ({ ...b, fontFamily: 'inherit', color: 'var(--foreground)' }),
  dropdownIndicator: b => ({ ...b, color: 'var(--muted-foreground)', padding: 6 }),
  clearIndicator: b => ({ ...b, color: 'var(--muted-foreground)', padding: 6 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

export function MSelect({ options, value, onChange, placeholder, clearLabel, style, noOptions = 'موردی نیست' }) {
  const opts = [{ value: '', label: clearLabel || placeholder || 'همه' }, ...(options || [])];
  const cur = opts.find(o => String(o.value) === String(value ?? '')) || opts[0];
  return (
    <div dir="rtl" style={{ minWidth: 150, flex: '1 1 150px', ...style }}>
      <Select options={opts} value={cur} onChange={o => onChange(o ? o.value : '')}
        placeholder={placeholder || 'انتخاب / جستجو...'} styles={selectStyles}
        noOptionsMessage={({ inputValue }) => inputValue ? 'موردی برای «' + inputValue + '» نیست' : noOptions}
        isClearable isSearchable />
    </div>
  );
}

/* ---- shamsi datepicker wrapper (API value = gregorian YYYY-MM-DD) ---- */
function toGregorian(dobj) {
  if (!dobj) return '';
  const d = new Date(dobj.toUnix() * 1000);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function MDate({ value, onChange, placeholder }) {
  return (
    <div style={{ flex: '1 1 150px', minWidth: 140 }}>
      <DatePicker
        calendar={persian} locale={persian_fa} format="YYYY/MM/DD"
        value={value ? new DateObject(new Date(value + 'T00:00:00')) : null}
        onChange={d => onChange(toGregorian(d))}
        placeholder={placeholder || 'انتخاب تاریخ'}
        inputClass="rmdp-input"
      />
    </div>
  );
}

/* ---- shamsi picker with JALALI value (YYYY/MM/DD) — matches birth_date convention ---- */
export function MDateJalali({ value, onChange, placeholder }) {
  return (
    <div style={{ flex: '1 1 150px', minWidth: 140 }}>
      <DatePicker
        calendar={persian} locale={persian_fa} format="YYYY/MM/DD"
        value={value ? new DateObject({ date: value, calendar: persian, locale: persian_fa, format: 'YYYY/MM/DD' }) : null}
        onChange={d => onChange(d ? d.format('YYYY/MM/DD') : '')}
        placeholder={placeholder || 'انتخاب تاریخ'}
        inputClass="rmdp-input"
      />
    </div>
  );
}

export function MSearch({ value, onChange, onEnter, placeholder }) {
  return (
    <div className="relative min-w-[180px] flex-[2_1_220px]">
      <Search size={16} className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
      <Input value={value} onChange={e => onChange(e.target.value)} onKeyDown={e => e.key === 'Enter' && onEnter && onEnter()}
        placeholder={placeholder} className="pl-9" />
    </div>
  );
}

export const faDate = v => { try { return new Date(v).toLocaleDateString('fa-IR'); } catch { return '-'; } };
export const faNum = v => Number(v || 0).toLocaleString('fa-IR');

/* ---- shared form primitives (vibefarsi Field/Input/Textarea) ---- */
export function MField({ label, children, hint }) {
  return (
    <Field label={label} hint={hint} className="min-w-0">{children}</Field>
  );
}

export function MInput({ style, className, ...rest }) {
  return <Input {...rest} className={className} style={style} />;
}

export function MTextarea({ style, className, ...rest }) {
  return <Textarea {...rest} className={className} style={style} />;
}

/* Section card inside modals */
export function MSection({ title, children, tone }) {
  const tones = {
    gray: 'bg-muted/50 border-border',
    amber: 'bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800',
    green: 'bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800',
    red: 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800',
  };
  return (
    <div className={cn('mb-3 rounded-xl border p-3.5', tones[tone || 'gray'])}>
      {title && <div className="mb-2.5 text-[14px] font-bold">{title}</div>}
      {children}
    </div>
  );
}

export function MGrid({ children, min = 160 }) {
  return <div className="grid gap-2.5" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))` }}>{children}</div>;
}

/* ---- user picker with live search (react-select, searchable) ----
 * value: user object (or null). onChange(user|null).
 * Searches GET /customers?q=...&per_page=10 with debounce. */
const userOptLabel = u => `${u.phone || ''}${u.name ? ' — ' + u.name : ''}`;
const toUserOpt = u => ({ value: u.id, label: userOptLabel(u), user: u });

export function MUserSelect({ value, onChange, placeholder, authHeaders }) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState(value ? [toUserOpt(value)] : []);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const lastQ = useRef('');

  const fetchUsers = useCallback(async (txt) => {
    const token = localStorage.getItem('admin_token');
    const headers = authHeaders || (token ? { Authorization: 'Bearer ' + token } : {});
    setLoading(true);
    try {
      const r = await axios.get(API_BASE + '/customers', { headers, params: { q: txt || '', per_page: 10 } });
      const list = r.data?.data || r.data || [];
      setOptions((Array.isArray(list) ? list : []).map(toUserOpt));
    } catch { /* keep old options */ }
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  useEffect(() => {
    if (value) setOptions(o => (o.some(x => x.value === value.id) ? o : [toUserOpt(value), ...o]));
  }, [value]);

  const onInputChange = (txt, meta) => {
    if (meta.action !== 'input-change') return;
    setInput(txt);
    lastQ.current = txt;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { if (lastQ.current === txt) fetchUsers(txt); }, 350);
  };

  return (
    <div dir="rtl">
      <Select
        options={options}
        value={value ? toUserOpt(value) : null}
        onChange={o => onChange(o ? o.user : null)}
        onInputChange={onInputChange}
        inputValue={input}
        onMenuOpen={() => { if (!options.length) fetchUsers(''); }}
        placeholder={placeholder || 'جستجوی کاربر با موبایل / نام...'}
        styles={selectStyles}
        noOptionsMessage={() => 'کاربری یافت نشد — عبارت دیگری جستجو کنید'}
        loadingMessage={() => 'در حال جستجو...'}
        isLoading={loading}
        isClearable
        isSearchable
        filterOption={null}
      />
    </div>
  );
}
